import { useCallback, useId, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { ShopperSearch } from '@/scapi';
import { SvgBookmarkAdd, SvgBookmarkFilled } from '@/components/icons-eataly';
import { useWishlist } from '@/hooks/use-wishlist';
import { useRequireAuth } from '@/hooks/use-require-auth';
import { buildAddToWishlistActionParams } from '@/lib/pending-wishlist-product';
import { useWishlistOverlay } from '@/providers/wishlist-overlay-context';
import { wishlistSelectionTriggerA11y } from '@/components/wishlist/wishlist-selection-drawer-a11y';
import { focusVisibleOutline } from '@/lib/focus-styles';
import { cn } from '@/lib/utils';
import { Button, type ButtonVariantOptions } from '../ui/button';
import { useCommerceEnabled } from '@/hooks/use-commerce-enabled';

export interface WishlistIconButtonProps {
    product: ShopperSearch.schemas['ProductSearchHit'];
    variant?: ShopperSearch.schemas['ProductSearchHit'];
    className?: string;
    tabIndex?: number;
    buttonVariant?: 'default' | 'small';
    size?: 'icon-small' | 'icon-large';
    hierarchy?: ButtonVariantOptions['hierarchy'];
}

export function WishlistIconButton({
    product,
    variant: variantProduct,
    className,
    tabIndex,
    size,
    hierarchy = 'tertiary',
    buttonVariant = 'default',
}: WishlistIconButtonProps) {
    const { t } = useTranslation('product');
    const { isItemInWishlist } = useWishlist();
    const { openWishlistSelectionDrawer, setOverlayTrigger, activeTriggerId, openOverlayId } = useWishlistOverlay();
    const commerceEnabled = useCommerceEnabled();
    const inList = isItemInWishlist(product, variantProduct);
    const triggerId = useId();
    const triggerA11y = wishlistSelectionTriggerA11y(activeTriggerId === triggerId ? openOverlayId : undefined);

    const handleOpenWishlist = useRequireAuth(
        () => {
            openWishlistSelectionDrawer({ product, variant: variantProduct });
            return Promise.resolve();
        },
        {
            actionName: 'addToWishlist',
            unauthenticatedBehavior: 'loginDrawer',
            getActionParams: () => buildAddToWishlistActionParams(product, variantProduct),
            getReturnUrl: () =>
                typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/',
            toastMessage: t('signInToAddToWishlist'),
        }
    );

    const handleClick = useCallback(
        (event: MouseEvent<HTMLButtonElement>) => {
            setOverlayTrigger({ id: triggerId, element: event.currentTarget });
            void handleOpenWishlist().catch(() => {
                // Auth required: rejected internally; login drawer is shown by useRequireAuth.
            });
        },
        [handleOpenWishlist, setOverlayTrigger, triggerId]
    );

    const aria = (inList ? t('inWishlist') : t('addToWishlist')) || '';

    if (!commerceEnabled) {
        return null;
    }

    if (buttonVariant === 'small') {
        return (
            <button
                {...triggerA11y}
                aria-label={aria}
                type="button"
                onClick={handleClick}
                className={cn(cn('flex shrink-0 cursor-pointer', focusVisibleOutline), className)}
                tabIndex={tabIndex}>
                {inList ? (
                    <SvgBookmarkFilled className={cn('size-6')} aria-hidden />
                ) : (
                    <SvgBookmarkAdd className={cn('size-6 hover:text-primary')} aria-hidden />
                )}
            </button>
        );
    } else {
        return (
            <Button
                {...triggerA11y}
                hierarchy={hierarchy}
                iconOnly
                size={size}
                aria-label={aria}
                onClick={handleClick}
                className={cn('cursor-pointer', className)}
                tabIndex={tabIndex}>
                {inList ? (
                    <SvgBookmarkFilled className="!size-6" aria-hidden />
                ) : (
                    <SvgBookmarkAdd className="!size-6" aria-hidden />
                )}
            </Button>
        );
    }
}
